import { useQuery } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget } from './BaseWidget'
import { Share2, Calendar, Clock } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { SocialMediaStatsData, ScheduledPostData } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'

// Social Media Stats Widget
export function SocialMediaStatsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'SOCIAL_MEDIA_STATS'],
    queryFn: () => dashboardConfigApi.getWidgetData<SocialMediaStatsData>('SOCIAL_MEDIA_STATS'),
  })

  const stats = data?.data

  return (
    <BaseWidget
      title={t('dashboard.socialMediaStats')}
      icon={<Share2 className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.total_calendars || 0}</p>
          <p className="text-xs text-muted-foreground">{t('socialMedia.calendars')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-500">{stats?.scheduled_posts || 0}</p>
          <p className="text-xs text-muted-foreground">{t('socialMedia.scheduled')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-500">{stats?.pending_approval || 0}</p>
          <p className="text-xs text-muted-foreground">{t('socialMedia.pending')}</p>
        </div>
      </div>
    </BaseWidget>
  )
}

// Scheduled Posts Widget
export function ScheduledPostsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'SCHEDULED_POSTS'],
    queryFn: () => dashboardConfigApi.getWidgetData<ScheduledPostData[]>('SCHEDULED_POSTS'),
  })

  const posts = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.scheduledPosts')}
      icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noScheduledPosts')}</p>
        ) : (
          posts.map((post) => (
            <Link
              key={post.post_id}
              to="/social-media"
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{post.post_title || post.post_content.substring(0, 50)}</p>
                <p className="text-xs text-muted-foreground truncate">{post.calendar_name}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-xs text-muted-foreground">{formatDate(post.scheduled_at || '')}</p>
                {post.platform && (
                  <Badge variant="outline" className="text-xs">{post.platform}</Badge>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Pending Approval Posts Widget
export function PendingApprovalWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'PENDING_APPROVAL'],
    queryFn: () => dashboardConfigApi.getWidgetData<ScheduledPostData[]>('PENDING_APPROVAL'),
  })

  const posts = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.pendingApproval')}
      icon={<Clock className="h-4 w-4 text-amber-500" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noPendingApproval')}</p>
        ) : (
          posts.map((post) => (
            <Link
              key={post.post_id}
              to="/social-media"
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{post.post_title || post.post_content.substring(0, 50)}</p>
                <p className="text-xs text-muted-foreground truncate">{post.calendar_name}</p>
              </div>
              <div className="text-right ml-2">
                <Badge variant="secondary" className="text-xs">{t('socialMedia.pendingApproval')}</Badge>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

